import { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";

interface UseResourceVerifierOptions<T> {
	id: string | number | null;
	validateIdFn: (id: string | number) => boolean;
	fetchResourceFn: () => Promise<AxiosResponse<T>>;
	validateResponseFn: (response: AxiosResponse<T>) => boolean;
	authHeader: Record<string, string>;
	timeoutMs?: number;
	maxAttempts?: number;
	rateLimitMs?: number;
	invalidIdMsg?: string;
	timeoutMsg?: string;
}

interface UseResourceVerifierReturn<T> {
	isLoading: boolean;
	resourceError: Error | null;
	resourceData: T | null;
}

function useResourceVerifier<T = any>({
	id,
	validateIdFn,
	fetchResourceFn,
	validateResponseFn,
	authHeader,
	timeoutMs = 5000,
	maxAttempts = 5,
	rateLimitMs = 1000,
	invalidIdMsg = "Invalid ID format",
	timeoutMsg = "Timeout. Resource may not exist or has been removed."
}: UseResourceVerifierOptions<T>): UseResourceVerifierReturn<T> {
	const [isLoading, setIsLoading] = useState(true);
	const [resourceError, setResourceError] = useState<Error | null>(null);
	const [resourceData, setResourceData] = useState<T | null>(null);
	
	const checkedRef = useRef(false);
	const requestAttempts = useRef(0);
	const lastRequestTime = useRef(0);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (checkedRef.current) {
			return;
		}

		if (!id || !validateIdFn(id)) {
			setResourceError(new Error(invalidIdMsg));
			setIsLoading(false);
			checkedRef.current = true;
			return;
		}

		const checkResourceExists = async () => {
			const now = Date.now();
			const timeSinceLastRequest = now - lastRequestTime.current;
			if (timeSinceLastRequest < rateLimitMs) {
				await new Promise(resolve => setTimeout(resolve, rateLimitMs - timeSinceLastRequest));
			}

			lastRequestTime.current = Date.now();
			requestAttempts.current++;

			try {
				const response = await fetchResourceFn();
				if (!validateResponseFn(response)) {
					throw new Error("Resource not found or invalid data received");
				}
				
				setResourceData(response.data);
				setIsLoading(false);
				checkedRef.current = true;
				
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
			} catch (error: any) {
				if (requestAttempts.current >= maxAttempts) {
					setResourceError(error.response?.status === 404 
						? new Error("Resource not found") 
						: new Error(error.message || "Error loading resource data"));
					setIsLoading(false);
					checkedRef.current = true;
					return;
				}

				setTimeout(checkResourceExists, rateLimitMs);
			}
		};

		timeoutRef.current = setTimeout(() => {
			if (!checkedRef.current) {
				setResourceError(new Error(timeoutMsg));
				setIsLoading(false);
				checkedRef.current = true;
			}
		}, timeoutMs);

		if (authHeader && Object.keys(authHeader).length > 0) {
			checkResourceExists();
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [id, authHeader, validateIdFn, fetchResourceFn, validateResponseFn, invalidIdMsg, timeoutMsg, maxAttempts, rateLimitMs, timeoutMs]);

	return { isLoading, resourceError, resourceData };
}

export default useResourceVerifier;
